import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["slide", "hint", "container", "dayPicker", "counter"]
  static values = {
    index: { type: Number, default: 0 },
    createUrl: { type: String, default: "" }
  }

  connect() {
    this.startX = 0
    this.startY = 0
    this.currentX = 0
    this.currentY = 0
    this.swiping = false
    this.direction = null // "horizontal" or "vertical"
    this.didSwipe = false // true if a swipe gesture was detected (suppress tap)
    this.pendingRecipeId = null
  }

  // --- Touch handlers (declared via data-action in HTML) ---

  touchstart(e) {
    this.startX = e.touches[0].clientX
    this.startY = e.touches[0].clientY
    this.touchTarget = e.target
    this.swiping = true
    this.direction = null
    this.didSwipe = false
  }

  touchmove(e) {
    if (!this.swiping) return
    this.currentX = e.touches[0].clientX
    this.currentY = e.touches[0].clientY
    const dx = this.currentX - this.startX
    const dy = this.startY - this.currentY // positive = up

    // Lock direction after 10px of movement
    if (!this.direction && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
      this.direction = Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical"
      this.didSwipe = true
    }

    if (!this.direction) return

    const slide = this.slideTargets[this.indexValue]
    if (!slide) return

    if (this.direction === "horizontal") {
      e.preventDefault()
      slide.style.transform = `translateX(${dx}px) rotate(${dx * 0.03}deg)`
      slide.style.transition = "none"
    } else if (this.direction === "vertical" && dy > 0) {
      e.preventDefault()
      const clamped = Math.min(dy, 120)
      slide.style.transform = `translateY(${-clamped * 0.5}px)`
      slide.style.transition = "none"
      // Show plan hint overlay
      const planOverlay = slide.querySelector("[data-plan-overlay]")
      if (planOverlay) planOverlay.style.opacity = Math.min(clamped / 80, 1)
    }
  }

  touchend(e) {
    if (!this.swiping) return
    this.swiping = false

    const dx = this.currentX - this.startX
    const dy = this.startY - (e.changedTouches?.[0]?.clientY ?? this.currentY)
    const slide = this.slideTargets[this.indexValue]
    if (!slide) return

    // Reset overlay
    const planOverlay = slide.querySelector("[data-plan-overlay]")
    if (planOverlay) planOverlay.style.opacity = 0

    if (this.direction === "horizontal") {
      if (Math.abs(dx) > 80) {
        // Animate out and advance
        const dir = dx > 0 ? 400 : -400
        slide.style.transition = "transform 0.3s ease-out, opacity 0.3s"
        slide.style.transform = `translateX(${dir}px) rotate(${dir * 0.03}deg)`
        slide.style.opacity = "0"
        setTimeout(() => {
          slide.style.transform = ""
          slide.style.opacity = ""
          slide.style.transition = ""
          dx > 0 ? this.prev() : this.next()
        }, 300)
        return
      }
    } else if (this.direction === "vertical" && dy > 80) {
      // Swipe up — open day picker
      slide.style.transform = ""
      slide.style.transition = ""
      const recipeId = slide.dataset.recipeId
      if (recipeId && this.hasDayPickerTarget) {
        this.openDayPicker(recipeId)
        return
      }
    }

    // No swipe detected — treat as tap to flip
    if (!this.didSwipe) {
      if (!this.touchTarget?.closest("button, a, form")) {
        this.touchHandledFlip = true
        this.flipCurrentSlide()
      }
      return
    }

    // Bounce back from incomplete swipe
    slide.style.transition = "transform 0.2s ease-out"
    slide.style.transform = ""
    setTimeout(() => { slide.style.transition = "" }, 200)
    this.direction = null
  }

  // --- Tap to flip between photo and recipe ---

  // Called via data-action="click->discover#flip" on photo/recipe views
  flip(e) {
    // Skip if a touch gesture just handled this (prevents double-fire on mobile)
    if (this.touchHandledFlip) { this.touchHandledFlip = false; return }
    if (e?.target?.closest("button, a, form")) return
    this.flipCurrentSlide()
  }

  flipCurrentSlide() {
    const slide = this.slideTargets[this.indexValue]
    if (!slide) return
    const photo = slide.querySelector("[data-photo-view]")
    const recipe = slide.querySelector("[data-recipe-view]")
    if (!photo || !recipe) return
    photo.classList.toggle("hidden")
    recipe.classList.toggle("hidden")
    // Hide action buttons when showing recipe view
    const actions = slide.querySelector("[data-photo-actions]")
    if (actions) actions.classList.toggle("hidden", photo.classList.contains("hidden"))
  }

  // --- Navigation ---

  keydown(e) {
    if (e.key === "ArrowLeft") { e.preventDefault(); this.prev() }
    else if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); this.next() }
    else if (e.key === "Escape") this.close()
  }

  next() {
    if (this.indexValue < this.slideTargets.length - 1) {
      this.resetFlip(this.slideTargets[this.indexValue])
      this.slideTargets[this.indexValue].classList.add("hidden")
      this.indexValue++
      this.slideTargets[this.indexValue].classList.remove("hidden")
      this.updateCounter()
    }
  }

  prev() {
    if (this.indexValue > 0) {
      this.resetFlip(this.slideTargets[this.indexValue])
      this.slideTargets[this.indexValue].classList.add("hidden")
      this.indexValue--
      this.slideTargets[this.indexValue].classList.remove("hidden")
      this.updateCounter()
    }
  }

  resetFlip(slide) {
    const photo = slide?.querySelector("[data-photo-view]")
    const recipe = slide?.querySelector("[data-recipe-view]")
    const actions = slide?.querySelector("[data-photo-actions]")
    if (photo) photo.classList.remove("hidden")
    if (recipe) recipe.classList.add("hidden")
    if (actions) actions.classList.remove("hidden")
  }

  updateCounter() {
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${this.indexValue + 1} / ${this.slideTargets.length}`
    }
  }

  close() {
    const link = this.element.querySelector("a[data-close]")
    if (link) window.location = link.href
  }

  // --- Day picker ---

  plan(e) {
    const recipeId = e.currentTarget.dataset.recipeId || this.slideTargets[this.indexValue]?.dataset.recipeId
    if (recipeId && this.hasDayPickerTarget) this.openDayPicker(recipeId)
  }

  openDayPicker(recipeId) {
    this.pendingRecipeId = recipeId
    this.dayPickerTarget.classList.remove("hidden")
  }

  closeDayPicker() {
    this.dayPickerTarget.classList.add("hidden")
    this.pendingRecipeId = null
  }

  selectDay(e) {
    const date = e.currentTarget.dataset.date
    if (!this.pendingRecipeId || !date || !this.createUrlValue) return

    const btn = e.currentTarget
    btn.disabled = true
    btn.innerHTML = '<svg class="h-4 w-4 text-warm-500 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>'

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.createUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token, Accept: "application/json" },
      body: JSON.stringify({ meal_plan_entry: { recipe_id: this.pendingRecipeId, planned_date: date, meal_type: "dinner" } })
    }).then(res => {
      if (res.ok) {
        btn.innerHTML = '<svg class="h-4 w-4 text-warm-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>'
        this.showToast("Added to meal plan")
        setTimeout(() => {
          this.closeDayPicker()
          this.next()
        }, 600)
      }
    })
  }

  // --- Toast ---

  showToast(message) {
    // Remove any existing toast
    this.element.querySelector("[data-toast]")?.remove()

    const toast = document.createElement("div")
    toast.setAttribute("data-toast", "")
    toast.className = "fixed top-[max(1rem,env(safe-area-inset-top))] left-1/2 -translate-x-1/2 z-50 rounded-full bg-white/95 dark:bg-gray-800/95 backdrop-blur-md px-5 py-2.5 shadow-lg ring-1 ring-warm-200 dark:ring-gray-700 text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2 opacity-0 translate-y-[-8px] transition-all duration-300"
    toast.innerHTML = `<svg class="h-4 w-4 text-warm-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>${message}`
    this.element.appendChild(toast)

    // Animate in
    requestAnimationFrame(() => {
      toast.style.opacity = "1"
      toast.style.transform = "translateX(-50%) translateY(0)"
    })

    // Auto dismiss
    setTimeout(() => {
      toast.style.opacity = "0"
      toast.style.transform = "translateX(-50%) translateY(-8px)"
      setTimeout(() => toast.remove(), 300)
    }, 2500)
  }
}
